import { formatDateWithPreset } from '@/helpers/dates';
import { useAppStore } from '@/stores/app';
import { useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';

/**
 * Validates and converts a value to a Date object.
 *
 * @param value - The value to convert to a Date
 * @returns A valid Date object or null if the value is invalid
 */
function toValidDate(value: Date | string | number | null | undefined): Date | null {
  if (!value) return null;

  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) return null;

  return date;
}

/**
 * Custom hook that provides a date formatting function based on user's
 * date format preference from the app store.
 *
 * This hook is reactive - when the user changes their date format preference
 * in settings, all components using this hook will automatically re-render
 * with the new format.
 *
 * @returns A function that formats dates according to user preference
 *
 * @example
 * function MyComponent() {
 *   const formatDate = useDateFormatter();
 *
 *   return <div>{formatDate(new Date())}</div>;
 * }
 *
 * @example
 * // For AG Grid valueFormatter
 * function MyTable() {
 *   const formatDate = useDateFormatter();
 *
 *   const colDefs = useMemo(() => [{
 *     field: 'created_at',
 *     valueFormatter: ({ value }) => formatDate(new Date(value))
 *   }], [formatDate]);
 * }
 */
export function useDateFormatter(): (value: Date | string | number | null | undefined) => string {
  const dateFormatPresetId = useAppStore(useShallow(state => state.dateFormatPresetId));

  const formatDate = useCallback(
    /**
     * Formats a date value according to the user's preference.
     *
     * @param value - The date value to format (Date object, ISO string, timestamp, or null/undefined)
     * @returns Formatted date string, or empty string if value is invalid
     */
    (value: Date | string | number | null | undefined): string => {
      const date = toValidDate(value);

      if (!date) return '';

      return formatDateWithPreset(date, dateFormatPresetId);
    },
    [dateFormatPresetId],
  );

  return formatDate;
}
